'use client';

import { useTranslations } from 'next-intl';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Credits } from '@/app/components/icons/Credits';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';

const FORM_SUBMIT_BUTTON_TEST_ID = 'form-submit-button';

interface TaskSubmitButtonProps {
  className?: string;
  disabled?: boolean;
  disabledTip?: ReactNode;
  submitting?: boolean;
  processing?: boolean;
  onClick?: () => void;
  extra?: ReactNode;
  displayCredits?: boolean;
  costCredits?: number;
  originalCredits?: number;
  creditsInsufficient?: boolean;
  // display options for credits indicator
  creditsVariant?: 'submitButton' | 'currentColor';
  creditsColor?: string;
  creditsPlacement?: 'afterChildren' | 'beforeChildren';
  creditsInsufficientConfig?: {
    tooltip: string;
    prefixText: ReactNode;
    actionText: ReactNode;
  };
}

export const TaskSubmitButton: FC<PropsWithChildren<TaskSubmitButtonProps>> = (props) => {
  const t = useTranslations();
  const isFree = useStore((state) => (state.userInfo?.subs_plan || 'free') === 'free');

  const {
    className,
    disabled,
    disabledTip,
    submitting,
    processing,
    onClick,
    children,
    extra,
    displayCredits = true,
    costCredits,
    originalCredits,
    creditsInsufficient,
    creditsVariant = 'currentColor',
    creditsColor = 'currentColor',
    creditsPlacement = 'afterChildren',
    creditsInsufficientConfig = {
      tooltip: t('text2audio.create.outOfCredit'),
      prefixText: t('create.createForm.error.creditsInsufficient'),
      actionText: isFree ? t('payments.navUpgrade') : t('payments.navPurchase'),
    },
  } = props;

  const isLoading = !!submitting || !!processing;
  const shouldUseDisabledLayout = !!disabledTip || (disabled && !processing);

  const renderCreditsInfo = () => {
    if (!displayCredits) return null;
    return (
      <>
        <Credits
          className={cn('shrink-0 text-base', creditsPlacement === 'afterChildren' && 'ml-1')}
          variant={creditsVariant}
        />
        {costCredits !== undefined && (
          <>
            <span style={{ color: creditsColor ?? 'inherit' }}>{costCredits}</span>
            {originalCredits && <span className="text-system-black line-through opacity-[.32]">{originalCredits}</span>}
          </>
        )}
      </>
    );
  };

  return shouldUseDisabledLayout ? (
    <CustomTooltip content={disabledTip} trigger="auto">
      <Button
        className={cn(
          'relative w-full cursor-not-allowed overflow-hidden border border-transparent bg-system-neutral02 text-lg text-system-white24 opacity-100 hover:bg-system-neutral02 data-[tour-highlight=true]:bg-ShengshuButton data-[tour-highlight=true]:text-black',
          className,
        )}
        size="lg"
        aria-disabled="true"
        data-testid={FORM_SUBMIT_BUTTON_TEST_ID}
      >
        <div className="flex items-center justify-center">
          {isLoading && <Loading className="mr-2 animate-spin text-black text-xl" />}
          <span>{children}</span>
        </div>
        {extra}
      </Button>
    </CustomTooltip>
  ) : (
    <CustomTooltip content={creditsInsufficient ? creditsInsufficientConfig.tooltip : ''} trigger="auto">
      <Button
        className={cn('relative w-full overflow-hidden text-lg disabled:cursor-not-allowed', className)}
        variant="primary"
        size="lg"
        disabled={isLoading}
        aria-disabled={isLoading ? 'true' : undefined}
        data-testid={FORM_SUBMIT_BUTTON_TEST_ID}
        onClick={onClick}
      >
        <div className="flex items-center justify-center gap-1">
          {isLoading && <Loading className="mr-1 animate-spin text-black text-xl" />}
          {creditsInsufficient ? (
            <>
              <span>{creditsInsufficientConfig.prefixText}</span>
              <Credits className="text-base" variant="currentColor" />
              <span>{creditsInsufficientConfig.actionText}</span>
            </>
          ) : (
            <>
              {creditsPlacement === 'beforeChildren' ? renderCreditsInfo() : null}
              <span>{children}</span>
              {creditsPlacement === 'afterChildren' ? renderCreditsInfo() : null}
            </>
          )}
        </div>
        {extra}
      </Button>
    </CustomTooltip>
  );
};

TaskSubmitButton.displayName = 'TaskSubmitButton';
